import type { GameConfig, PlayerOrTeamId, RoundId, Session, SessionId } from '@/schemas';
import type { SessionStore } from './session-store.types';

export const applyRoundNoteToDraft = (
  draft: SessionStore,
  sessionId: SessionId,
  roundId: RoundId,
  value?: string,
): void => {
  if (draft.activeSession?.id !== sessionId) {
    return;
  }
  const targetRound = draft.activeSession.rounds.find((round) => round.id === roundId);
  if (targetRound) {
    targetRound.notes = value;
  }
};

export const addRoundTagToDraft = (
  draft: SessionStore,
  sessionId: SessionId,
  roundId: RoundId,
  tag: string,
): void => {
  if (draft.activeSession?.id !== sessionId) {
    return;
  }
  const targetRound = draft.activeSession.rounds.find((round) => round.id === roundId);
  if (!targetRound) {
    return;
  }
  if (!targetRound.tags) {
    targetRound.tags = [];
  }
  if (!targetRound.tags.includes(tag)) {
    targetRound.tags.push(tag);
  }
};

export const removeRoundTagFromDraft = (
  draft: SessionStore,
  sessionId: SessionId,
  roundId: RoundId,
  tag: string,
): void => {
  if (draft.activeSession?.id !== sessionId) {
    return;
  }
  const targetRound = draft.activeSession.rounds.find((round) => round.id === roundId);
  if (!targetRound || !targetRound.tags) {
    return;
  }
  targetRound.tags = targetRound.tags.filter((candidate) => candidate !== tag);
};

export const setRoundTagsOnDraft = (
  draft: SessionStore,
  sessionId: SessionId,
  roundId: RoundId,
  tags: string[] | undefined,
): void => {
  if (draft.activeSession?.id !== sessionId) {
    return;
  }
  const targetRound = draft.activeSession.rounds.find((round) => round.id === roundId);
  if (targetRound) {
    targetRound.tags = tags ? [...tags] : undefined;
  }
};

export const setSessionNameOnDraft = (
  draft: SessionStore,
  sessionId: SessionId,
  name: string | undefined,
): void => {
  if (draft.activeSession?.id !== sessionId) {
    return;
  }
  if (typeof name === 'string') {
    draft.activeSession.name = name;
  }
};

export const setSessionNotesOnDraft = (
  draft: SessionStore,
  sessionId: SessionId,
  notes: string | undefined,
): void => {
  if (draft.activeSession?.id !== sessionId) {
    return;
  }
  draft.activeSession.notes = notes;
};

export const setSessionGameConfigOnDraft = (
  draft: SessionStore,
  sessionId: SessionId,
  config: GameConfig,
): void => {
  if (draft.activeSession?.id !== sessionId) {
    return;
  }
  draft.activeSession.gameConfigSnapshot = config;
};

export const setSessionWinnerOnDraft = (
  draft: SessionStore,
  sessionId: SessionId,
  winner: PlayerOrTeamId | undefined,
): void => {
  if (draft.activeSession?.id !== sessionId) {
    return;
  }
  draft.activeSession.winner = winner;
};

export const addSessionTagOnDraft = (
  draft: SessionStore,
  sessionId: SessionId,
  tag: string,
): void => {
  if (draft.activeSession?.id !== sessionId) {
    return;
  }
  if (!draft.activeSession.tags) {
    draft.activeSession.tags = [];
  }
  if (!draft.activeSession.tags.includes(tag)) {
    draft.activeSession.tags.push(tag);
  }
};

export const setSessionTagsOnDraft = (
  draft: SessionStore,
  sessionId: SessionId,
  tags: string[] | undefined,
): void => {
  if (draft.activeSession?.id !== sessionId) {
    return;
  }
  draft.activeSession.tags = tags ? [...tags] : undefined;
};

export const removeSessionTagOnDraft = (
  draft: SessionStore,
  sessionId: SessionId,
  tag: string,
): void => {
  if (draft.activeSession?.id !== sessionId || !draft.activeSession.tags) {
    return;
  }
  draft.activeSession.tags = draft.activeSession.tags.filter((candidate) => candidate !== tag);
};

export const clearActiveSessionOnDraft = (draft: SessionStore): void => {
  draft.activeSession = undefined;
  draft.canUndo = false;
  draft.canRedo = false;
};

export const restoreActiveSessionOnDraft = (draft: SessionStore, session: Session): void => {
  draft.activeSession = session;
  draft.canUndo = false;
  draft.canRedo = false;
};
